/**
 * Copyright 2019, OpenCensus Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import * as webTypes from '@opencensus/web-types';
import { randomSpanId } from '../../common/id-util';
import { getDateForPerfTime } from '../../common/time-util';
/** Default span name if none is specified. */
var DEFAULT_SPAN_NAME = 'unnamed';
/** A span represents a single operation within a trace. */
var Span = /** @class */ (function () {
    function Span(
    /** The ID of this span. Defaults to a random span ID. */
    id) {
        if (id === void 0) { id = randomSpanId(); }
        this.id = id;
        /** If the parent span is in another process. */
        this.remoteParent = false;
        /** The span ID of this span's parent. If it's a root span, must be empty */
        this.parentSpanId = '';
        /** Trace id asscoiated with span. */
        this.traceId = '';
        /** Trace state associated with span */
        this.traceState = '';
        /** The display name of the span. */
        this.name = DEFAULT_SPAN_NAME;
        /** Kind of span. */
        this.kind = webTypes.SpanKind.UNSPECIFIED;
        /** An object to log information to. Logs to the JS console by default. */
        this.logger = console;
        /**
         * Status associated with this span. Defaults to OK status. Note that the
         * `code` is not an HTTP status, but is a specific trace status code. See:
         * https://github.com/census-instrumentation/opencensus-specs/blob/master/trace/HTTP.md#mapping-from-http-status-codes-to-trace-status-codes
         */
        this.status = { code: webTypes.CanonicalCode.OK };
        /** A set of attributes, each in the format [KEY]:[VALUE] */
        this.attributes = {};
        /** Text annotations with a set of attributes. */
        this.annotations = [];
        /** Event describing messages sent/received between Spans. */
        this.messageEvents = [];
        /** Pointers from the current span to another span */
        this.links = [];
        /** Start time of the span as measured by the browser performance clock. */
        this.startPerfTime = 0;
        /** A list of child spans. */
        this.spans = [];
        /**
         * Number of dropped attributes. This is always zero because OpenCensus web
         * does not implement attribute dropping (but may be done by agent on export).
         */
        this.droppedAttributesCount = 0;
        /** Number of dropped links. Always zero for OpenCensus web. */
        this.droppedLinksCount = 0;
        /** Number of dropped annotations. Always zero for OpenCensus web. */
        this.droppedAnnotationsCount = 0;
        /** Number of dropped message events. Always zero for OpenCensus web. */
        this.droppedMessageEventsCount = 0;
        /**
         * Trace parameter configuration. Not used by OpenCensus Web, but
         * kept for interface compatibility with @opencensus/web-types.
         */
        this.activeTraceParams = {};
        /**
         * Indicates if span was started. This is always true for opencensus-web for
         * code simplicity purposes but kept for interface compatibility with
         * @opencensus/web-types.
         */
        this.started = true;
        /** End time of the span as measured by the browser performance clock. */
        this.endPerfTime = 0;
        this.numberOfChildrenLocal = 0;
    }
    Object.defineProperty(Span.prototype, "isRootSpan", {
        /** Whether this span is a RootSpan */
        get: function () {
            return this.parentSpanId === '';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Span.prototype, "startTime", {
        /** Start time of the span as a Date. */
        get: function () {
            return getDateForPerfTime(this.startPerfTime);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Span.prototype, "numberOfChildren", {
        /** Gets the number of child span created for this span. */
        get: function () {
            return this.numberOfChildrenLocal;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Span.prototype, "endTime", {
        /** End time of the span as a Date. */
        get: function () {
            return getDateForPerfTime(this.endPerfTime);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Span.prototype, "ended", {
        /** Indicates if span was ended. */
        get: function () {
            return this.endPerfTime > 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Span.prototype, "duration", {
        /** Span duration in milliseconds. */
        get: function () {
            return this.endPerfTime - this.startPerfTime;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Span.prototype, "spanContext", {
        /** Gives the TraceContext of the span. */
        get: function () {
            return {
                traceId: this.traceId,
                spanId: this.id,
                options: 0x1,
                traceState: this.traceState,
            };
        },
        enumerable: true,
        configurable: true
    });
    /** Recursively gets the descendant spans. */
    Span.prototype.allDescendants = function () {
        return this.spans.reduce(function (acc, cur) {
            acc.push(cur);
            var desc = cur.allDescendants();
            acc = acc.concat(desc);
            return acc;
        }, []);
    };
    /**
     * Starts a new child span.
     * @param [options] A SpanOptions object to start a child span.
     */
    Span.prototype.startChildSpan = function (options) {
        this.numberOfChildrenLocal++;
        var child = new Span();
        child.traceId = this.traceId;
        child.traceState = this.traceState;
        if (options && options.name)
            child.name = options.name;
        if (options && options.kind)
            child.kind = options.kind;
        child.start();
        child.parentSpanId = this.id;
        this.spans.push(child);
        return child;
    };
    /**
     * Adds an attribute to the span.
     * @param key Describes the value added.
     * @param value What value to set for the attribute. If the value is a typeof object
     *        it has to be JSON.stringify-able, cannot contain circular dependencies.
     */
    Span.prototype.addAttribute = function (key, value) {
        var serializedValue = typeof value === 'object' ? JSON.stringify(value) : value;
        this.attributes[key] = serializedValue;
    };
    /**
     * Adds an annotation to the span.
     * @param description Describes the event.
     * @param attributes A set of attributes on the annotation.
     * @param timestamp A timestamp in browser performance clock milliseconds.
     *     Defaults to `performance.now()`.
     */
    Span.prototype.addAnnotation = function (description, attributes, timestamp) {
        if (attributes === void 0) { attributes = {}; }
        if (timestamp === void 0) { timestamp = performance.now(); }
        this.annotations.push({ description: description, attributes: attributes, timestamp: timestamp });
    };
    /**
     * Adds a link to the span.
     * @param traceId The trace ID for a trace within a project.
     * @param spanId The span ID for a span within a trace.
     * @param type The relationship of the current span relative to the linked.
     * @param attributes A set of attributes on the link.
     */
    Span.prototype.addLink = function (traceId, spanId, type, attributes) {
        if (attributes === void 0) { attributes = {}; }
        this.links.push({ traceId: traceId, spanId: spanId, type: type, attributes: attributes });
    };
    /**
     * Adds a message event to the span.
     * @param type The type of message event.
     * @param id An identifier for the message event.
     * @param timestamp A time in browser performance clock milliseconds.
     *     Defaults to `performance.now()`.
     */
    Span.prototype.addMessageEvent = function (type, id, timestamp, uncompressedSize, compressedSize) {
        if (timestamp === void 0) { timestamp = performance.now(); }
        this.messageEvents.push({
            type: type,
            id: id,
            timestamp: timestamp,
            uncompressedSize: uncompressedSize,
            compressedSize: compressedSize,
        });
    };
    /**
     * Sets a status to the span.
     * @param code The canonical status code.
     * @param message optional A developer-facing error message.
     */
    Span.prototype.setStatus = function (code, message) {
        this.status = { code: code, message: message };
    };
    /** Starts span by setting `startTime` to now. */
    Span.prototype.start = function () {
        this.startPerfTime = performance.now();
    };
    /** Ends the span by setting `endTime` to now. */
    Span.prototype.end = function () {
        if (this.endPerfTime === 0) {
            this.endPerfTime = performance.now();
        }
    };
    /** Forces the span to end. Same as `end` for opencensus-web. */
    Span.prototype.truncate = function () {
        this.end();
    };
    return Span;
}());
export { Span };
//# sourceMappingURL=span.js.map